import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { fetchAdZones } from "@/services/adZoneService";
import { AdZone, AdZoneKey } from "@/types/adZones";
import { useAuth } from "@/hooks/useAuth";
import { isAdminEmail } from "@/constants/admin";

interface AdZonesContextValue {
  zones: Record<AdZoneKey, AdZone | null>;
  loading: boolean;
  refresh: () => Promise<void>;
}

const AdZonesContext = createContext<AdZonesContextValue | null>(null);

const emptyZones = (): Record<AdZoneKey, AdZone | null> => ({
  banner: null,
  sidebar: null,
  footer: null,
});

export const AdZonesProvider = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAuth();
  const [zones, setZones] = useState<Record<AdZoneKey, AdZone | null>>(emptyZones);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    const includeInactive = isAdminEmail(user?.email);
    const data = await fetchAdZones(includeInactive);

    const mapped = emptyZones();
    data.forEach((zone) => {
      mapped[zone.zone_key] = zone;
    });
    setZones(mapped);
    setLoading(false);
  }, [user?.email]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = useMemo(
    () => ({ zones, loading, refresh }),
    [zones, loading, refresh]
  );

  return (
    <AdZonesContext.Provider value={value}>
      {children}
    </AdZonesContext.Provider>
  );
};

export const useAdZones = () => {
  const context = useContext(AdZonesContext);
  if (!context) {
    throw new Error("useAdZones must be used within AdZonesProvider");
  }
  return context;
};

export const useAdZone = (key: AdZoneKey) => {
  const { zones, loading } = useAdZones();
  return { zone: zones[key], loading };
};
